import { BoardProfile, BoardResult, BoardSkills, CityGuide, Claim, coarsePoint, DirectoryEntry, Envelope, EstimateResponse, GeoResult, MatchResponse, MessageId, MyMentorListing, MyQr, PushKey, QrPerson, Session, } from "@gigsync/protocol";
import { z } from "zod";
import { BEARER_PREFIX, ContentType, Header, HttpMethod } from "../http.js";
import { log } from "../log.js";
import { REQUEST_TIMEOUT_MS, withDeadline } from "./deadline.js";
import { HttpError } from "./errors.js";
import { ApiPath } from "./paths.js";
export { REQUEST_TIMEOUT_MS } from "./deadline.js";
export { HttpError, NetError, NetReason } from "./errors.js";
export { ApiPath };
const ErrorBody = z.object({ error: z.string() });
const Removed = z.object({ removed: z.boolean() });
/** Just enough of an envelope this phone cannot read to acknowledge it. */
const Identified = z.object({ id: MessageId });
/** Typed calls to workers/api. Every response is parsed with its protocol schema before it is used. */
export class ApiClient {
    base;
    fetchFn;
    session;
    constructor(base, fetchFn = (input, init) => fetch(input, init), session) {
        this.base = base;
        this.fetchFn = fetchFn;
        this.session = session;
    }
    /** The same client, acting as a signed-in device. */
    as(session) {
        return new ApiClient(this.base, this.fetchFn, session);
    }
    async requestOtp(phone) {
        await this.call(HttpMethod.post, ApiPath.otpRequest, { phone });
    }
    async verifyOtp(phone, code, device) {
        return Session.parse(await this.call(HttpMethod.post, ApiPath.otpVerify, { phone, code, device }));
    }
    async directory(user) {
        return DirectoryEntry.parse(await this.call(HttpMethod.get, `${ApiPath.directory}${user}`));
    }
    deliver(envelope) {
        return this.call(HttpMethod.post, `${ApiPath.mailbox}${envelope.to.user}`, envelope);
    }
    /**
     * What the mailbox holds, envelope by envelope: one this phone cannot read (a kind from a newer app) is skipped and
     * acknowledged here, so it neither blocks the others nor comes back.
     */
    async inbox() {
        const fetched = z.array(z.unknown()).parse(await this.call(HttpMethod.get, ApiPath.inbox));
        const readable = [];
        const unreadable = [];
        for (const raw of fetched) {
            const parsed = Envelope.safeParse(raw);
            if (parsed.success) {
                readable.push(parsed.data);
                continue;
            }
            const id = Identified.safeParse(raw).data?.id;
            log.warn("unreadable envelope skipped", { messageId: id });
            if (id)
                unreadable.push(id);
        }
        if (unreadable.length > 0)
            await this.ackUnreadable(unreadable);
        return readable;
    }
    /** A failed acknowledgement only means the skipped envelopes come back once more; the readable ones still go on. */
    async ackUnreadable(ids) {
        try {
            await this.ack(ids);
        }
        catch (err) {
            log.warn("ack of unreadable envelopes failed", {
                count: ids.length,
                error: err instanceof Error ? err.message : String(err),
            });
        }
    }
    ack(ids) {
        return this.call(HttpMethod.post, ApiPath.ack, { ids });
    }
    async claim(engagement) {
        return Claim.parse(await this.call(HttpMethod.post, ApiPath.claims, { engagement })).owner;
    }
    publishProfile(profile) {
        return this.call(HttpMethod.put, ApiPath.boardProfile, profile);
    }
    async removeProfile() {
        return Removed.parse(await this.call(HttpMethod.delete, ApiPath.boardProfile)).removed;
    }
    /** One worker's card as the board serves it (record capped); undefined when they are not on the board. */
    async workerCard(user) {
        try {
            return BoardProfile.parse(await this.call(HttpMethod.get, `${ApiPath.boardWorkers}/${user}`));
        }
        catch (err) {
            if (err instanceof HttpError && err.status === 404)
                return undefined;
            throw err;
        }
    }
    async searchWorkers(query) {
        const params = new URLSearchParams(Object.entries(query)
            .filter(([, v]) => v !== undefined)
            .map(([k, v]) => [k, String(v)]));
        return BoardResult.parse(await this.call(HttpMethod.get, `${ApiPath.boardWorkers}?${params}`)).workers;
    }
    async boardSkills() {
        return BoardSkills.parse(await this.call(HttpMethod.get, ApiPath.boardSkills)).skills;
    }
    async publishMentor(listing) {
        return MyMentorListing.parse(await this.call(HttpMethod.put, ApiPath.mentorMe, listing));
    }
    /** Answers 204 whether or not a listing was there: either way the owner is off the list. */
    async removeMentor() {
        await this.call(HttpMethod.delete, ApiPath.mentorMe);
    }
    async matchMentors(query) {
        return MatchResponse.parse(await this.call(HttpMethod.post, ApiPath.mentorMatch, query)).mentors;
    }
    reportMentor(report) {
        return this.call(HttpMethod.post, ApiPath.mentorReport, report);
    }
    blockMentor(user) {
        return this.call(HttpMethod.post, ApiPath.mentorBlock, { user });
    }
    /** Tells the directory this device speaks `protocol` now, after an app update. */
    raiseProtocol(protocol) {
        return this.call(HttpMethod.put, ApiPath.deviceProtocol, { protocol });
    }
    /** The key to subscribe to pushes with, or undefined while push is not set up on the API. */
    async pushKey() {
        return PushKey.parse(await this.call(HttpMethod.get, ApiPath.pushKey)).public_key;
    }
    /**
     * Tells the API where this device's browser takes pushes and the keys to encrypt them to; only a real push service's
     * address is kept.
     */
    async subscribePush(subscription) {
        await this.call(HttpMethod.put, ApiPath.devicePush, subscription);
    }
    async unsubscribePush() {
        await this.call(HttpMethod.delete, ApiPath.devicePush);
    }
    /** The usual range for a job, or why there is none ("no estimate" is an answer, not an error). */
    async estimate(request) {
        return EstimateResponse.parse(await this.call(HttpMethod.post, ApiPath.estimates, request));
    }
    /** The places at a point, which is rounded to the coarse grid here, before it leaves the phone (ADR 0045). */
    async reverseGeocode(point) {
        const p = coarsePoint(point);
        const params = new URLSearchParams({ lat: String(p.lat), lon: String(p.lon) });
        return GeoResult.parse(await this.call(HttpMethod.get, `${ApiPath.geoReverse}?${params}`)).places;
    }
    /** Places matching what the owner typed. */
    async searchPlaces(q) {
        const params = new URLSearchParams({ q });
        return GeoResult.parse(await this.call(HttpMethod.get, `${ApiPath.geoSearch}?${params}`)).places;
    }
    /** A city's guide, checked against its schema before the landing journey keeps it. */
    async cityGuide(city) {
        return CityGuide.parse(await this.call(HttpMethod.get, `${ApiPath.content}${city}`));
    }
    async myQr(owner) {
        return MyQr.parse(await this.call(HttpMethod.put, ApiPath.qrMe, owner));
    }
    async replaceQr(owner) {
        return MyQr.parse(await this.call(HttpMethod.post, ApiPath.qrReplace, owner));
    }
    async lookupQr(code) {
        return QrPerson.parse(await this.call(HttpMethod.get, `${ApiPath.qr}${code}`));
    }
    async takeOffer(code) {
        await this.call(HttpMethod.post, `${ApiPath.qr}${code}${ApiPath.qrOffer}`);
    }
    async call(method, path, body) {
        const headers = { [Header.contentType]: ContentType.json };
        if (this.session)
            headers[Header.authorization] = `${BEARER_PREFIX}${this.session.token}`;
        const init = { method, headers, ...(body === undefined ? {} : { body: JSON.stringify(body) }) };
        const { res, json } = await withDeadline(REQUEST_TIMEOUT_MS, async (signal) => {
            const res = await this.fetchFn(`${this.base}${path}`, { ...init, signal });
            const json = await res.json().catch(() => null);
            return { res, json };
        });
        if (!res.ok)
            throw new HttpError(res.status, ErrorBody.safeParse(json).data?.error ?? res.statusText);
        return json;
    }
}
//# sourceMappingURL=api.js.map